import { useState } from 'react';
import type { Order } from '@/mocks/orders';
import { statusLabels, statusColors } from '@/mocks/orders';

interface OrderDetailPanelProps {
  order: Order | null;
  onClose: () => void;
}

const productionSteps = [
  { key: 'yeni', label: 'Yeni Sipariş', icon: 'ri-checkbox-circle-line' },
  { key: 'cizimde', label: 'Çizim Aşaması', icon: 'ri-pencil-ruler-line' },
  { key: 'cizim_onay', label: 'Çizim Onayı', icon: 'ri-check-double-line' },
  { key: 'kesimde', label: 'Kesim Aşaması', icon: 'ri-scissors-cut-line' },
  { key: 'aydinlatma', label: 'Aydınlatma / Montaj', icon: 'ri-lightbulb-line' },
  { key: 'test', label: 'Test Aşaması', icon: 'ri-flashlight-line' },
  { key: 'paketleme', label: 'Paketleme', icon: 'ri-box-3-line' },
  { key: 'kargoya_hazir', label: 'Kargoya Hazır', icon: 'ri-check-line' },
  { key: 'kargoda', label: 'Kargoya Verildi', icon: 'ri-truck-line' },
  { key: 'teslim', label: 'Teslim Edildi', icon: 'ri-home-line' },
];

const productTypeLabels: Record<string, string> = {
  isikli_tablo: 'İç Mekan Işıklı Tablo',
  fener_tabela: 'Fener Tabela',
  mdf_dekoratif: 'MDF Dekoratif Ürün',
  arac_logo_set: '12 Adet MDF Araç Logosu',
  ozel_tasarim: 'Özel Tasarım',
};

const orderSourceLabels: Record<string, string> = {
  instagram_dm: 'Instagram DM',
  whatsapp: 'WhatsApp',
  reklam: 'Reklam',
  tavsiye: 'Tavsiye',
  manuel: 'Manuel',
};

const mockMessages = [
  { id: 1, type: 'sent', content: 'Merhaba Ahmet Bey, siparişiniz kesim aşamasına alınmıştır.', time: '14:20', date: '17.06.2026' },
  { id: 2, type: 'sent', content: 'Tasarım onayınız alınmıştır, teşekkür ederiz.', time: '11:45', date: '17.06.2026' },
  { id: 3, type: 'received', content: 'Tamamdir cok tesekkurler, bekliyorum', time: '11:40', date: '17.06.2026' },
  { id: 4, type: 'sent', content: 'Merhaba Ahmet Bey, çizim tamamlandı. Onayınız için görseli iletiyorum.', time: '10:15', date: '17.06.2026' },
];

export default function OrderDetailPanel({ order, onClose }: OrderDetailPanelProps) {
  const [statusDropdown, setStatusDropdown] = useState(false);
  const [showCargoForm, setShowCargoForm] = useState(false);
  const [cargoCompany, setCargoCompany] = useState('');
  const [cargoTracking, setCargoTracking] = useState('');

  if (!order) return null;

  const currentStepIndex = productionSteps.findIndex(s => s.key === order.status);
  const isCancelledOrProblem = order.status === 'iptal' || order.status === 'sorunlu';

  const getMessageTemplate = (type: string) => {
    const templates: Record<string, string> = {
      daily: `Merhaba ${order.customerName}, Tablocum.11 siparişinizin güncel durumu: ${statusLabels[order.status]}. Ürününüz şu anda ${order.productionStage} aşamasındadır. Her aşama tamamlandığında sizi bilgilendireceğiz.`,
      drawing: `Merhaba ${order.customerName}, siparişiniz için çizim aşamasına geçilmiştir. Tasarım hazırlandığında onayınız için size iletilecektir.`,
      cutting: `Merhaba ${order.customerName}, onaylanan ürününüz kesim aşamasına alınmıştır. Üretim süreci devam etmektedir.`,
      lighting: `Merhaba ${order.customerName}, ürününüz aydınlatma ve test aşamasındadır. Son kontroller tamamlandıktan sonra paketleme sürecine geçilecektir.`,
      cargo: `Merhaba ${order.customerName}, Tablocum.11 siparişiniz kargoya verilmiştir. Kargo firması: ${order.cargoCompany || 'Bilgi eklenecek'}. Takip numarası: ${order.cargoTrackingNo || 'Bilgi eklenecek'}. Ürün teslim sürecinizi bu takip numarası ile kontrol edebilirsiniz.`,
      thanks: `Merhaba ${order.customerName}, Tablocum.11 siparişinizin size ulaşmış olmasını umuyoruz. Güzel günlerde kullanmanız dileğiyle. Memnuniyetinizi bizimle paylaşmanız bizi mutlu eder.`,
    };
    return templates[type] || '';
  };

  const openWhatsApp = (message: string) => {
    window.open(`https://wa.me/${order.phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-[640px] bg-white z-50 shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold text-stone-900">{order.id}</h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status]}`}>
                {statusLabels[order.status]}
              </span>
              {order.isUrgent && (
                <span className="text-xs bg-status-red/10 text-status-red px-2 py-0.5 rounded font-medium pulse-warning">ACİL</span>
              )}
            </div>
            <p className="text-sm text-stone-500 mt-0.5">{order.customerName} · {order.orderDate}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Info */}
          <section>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Müşteri Bilgileri</h4>
            <div className="bg-stone-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-stone-800">{order.customerName}</span>
                <a
                  href={`https://wa.me/${order.phone}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-whatsapp-line" /> {order.phone.replace('90', '0')}
                </a>
              </div>
              <p className="text-xs text-stone-500">
                <i className="ri-map-pin-line mr-1" />
                {order.city}/{order.district}
              </p>
              <p className="text-xs text-stone-500">{order.address}</p>
            </div>
          </section>

          {/* Order Info */}
          <section>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Sipariş Bilgileri</h4>
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-stone-400 block">Ürün</span>
                  <span className="text-stone-800 font-medium">{order.productName}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Ürün Tipi</span>
                  <span className="text-stone-800">{productTypeLabels[order.productType] || order.productType}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Adet</span>
                  <span className="text-stone-800">{order.quantity}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Tutar</span>
                  <span className="text-stone-800 font-bold">{order.amount.toLocaleString('tr-TR')} ₺</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Ödeme Tipi</span>
                  <span className="text-stone-800">{order.paymentType === 'kapida_odeme' ? 'Kapıda Ödeme' : order.paymentType === 'havale' ? 'Havale/EFT' : 'Kredi Kartı'}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Tahsilat Durumu</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    order.paymentStatus === 'tahsil_edildi' ? 'bg-status-green/10 text-status-green' :
                    order.paymentStatus === 'bekleniyor' ? 'bg-status-orange/10 text-status-orange' :
                    'bg-status-red/10 text-status-red'
                  }`}>
                    {order.paymentStatus === 'tahsil_edildi' ? 'Tahsil Edildi' : order.paymentStatus === 'bekleniyor' ? 'Bekleniyor' : order.paymentStatus === 'kismi_tahsil' ? 'Kısmi' : order.paymentStatus === 'iade' ? 'İade' : 'İptal'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Sipariş Tarihi</span>
                  <span className="text-stone-800">{order.orderDate}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Tahmini Teslim</span>
                  <span className={`${order.isLate ? 'text-status-red font-medium' : 'text-stone-800'}`}>{order.estimatedDelivery}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Sipariş Kaynağı</span>
                  <span className="text-stone-800">{orderSourceLabels[order.orderSource]}</span>
                </div>
                <div>
                  <span className="text-xs text-stone-400 block">Logo Dosyası</span>
                  <span className="text-stone-800">{order.hasLogoFile ? 'Var' : 'Yok'}</span>
                </div>
              </div>
              {order.customerRequest && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <span className="text-xs text-stone-400 block mb-1">Müşteri Özel İsteği</span>
                  <p className="text-sm text-stone-700">{order.customerRequest}</p>
                </div>
              )}
              {order.note && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <span className="text-xs text-stone-400 block mb-1">Not</span>
                  <p className="text-sm text-stone-700">{order.note}</p>
                </div>
              )}
            </div>
          </section>

          {/* Production Stages */}
          <section>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Üretim Aşamaları</h4>
            <div className="bg-stone-50 rounded-xl p-4">
              {isCancelledOrProblem ? (
                <div className="text-center py-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                    {statusLabels[order.status]}
                  </span>
                  <p className="text-sm text-stone-500 mt-2">{order.productionStage}</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {productionSteps.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    const isPending = index > currentStepIndex;

                    return (
                      <div key={step.key} className="flex items-start gap-3">
                        {/* Timeline line + dot */}
                        <div className="flex flex-col items-center">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                            isCompleted ? 'bg-status-green text-white' :
                            isCurrent ? 'bg-gold-500 text-white' :
                            'bg-stone-200 text-stone-400'
                          }`}>
                            {isCompleted ? <i className="ri-check-line" /> : index + 1}
                          </div>
                          {index < productionSteps.length - 1 && (
                            <div className={`w-0.5 flex-1 min-h-[20px] ${
                              isCompleted ? 'bg-status-green/40' : 'bg-stone-200'
                            }`} />
                          )}
                        </div>
                        <div className={`pb-4 ${isPending ? 'opacity-40' : ''}`}>
                          <span className={`text-sm font-medium block ${
                            isCurrent ? 'text-gold-600' : 'text-stone-800'
                          }`}>
                            {step.label}
                          </span>
                          {isCurrent && (
                            <span className="text-xs text-gold-500 mt-0.5 block">{order.productionStage}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Cargo Info */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Kargo Bilgileri</h4>
              {!order.cargoTrackingNo && order.status !== 'iptal' && (
                <button
                  onClick={() => setShowCargoForm(!showCargoForm)}
                  className="text-xs text-gold-600 hover:text-gold-700 font-medium cursor-pointer"
                >
                  + Kargo Takip Ekle
                </button>
              )}
            </div>
            {order.cargoTrackingNo ? (
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-xs text-stone-400 block">Kargo Firması</span>
                    <span className="text-stone-800 font-medium">{order.cargoCompany}</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Takip No</span>
                    <span className="text-stone-800 font-mono text-xs">{order.cargoTrackingNo}</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Durum</span>
                    <span className="text-stone-800">{order.cargoStatus ? cargoStatusLabel(order.cargoStatus) : '-'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 block">Kargo Tarihi</span>
                    <span className="text-stone-800">{order.cargoDate || '-'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <p className="text-sm text-stone-400">
                  {order.status === 'kargoya_hazir' ? 'Kargo takip numarası eklenmemiş!' : 'Henüz kargo bilgisi yok'}
                </p>
                {order.status === 'kargoya_hazir' && (
                  <p className="text-xs text-status-red mt-1">Kargoya hazır görünen bu sipariş için takip numarası ekleyin.</p>
                )}
              </div>
            )}

            {showCargoForm && (
              <div className="mt-3 bg-stone-50 rounded-xl p-4 border border-stone-200">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-stone-500 block mb-1">Kargo Firması</label>
                    <select
                      value={cargoCompany}
                      onChange={(e) => setCargoCompany(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400"
                    >
                      <option value="">Seçiniz</option>
                      <option value="MNG Kargo">MNG Kargo</option>
                      <option value="Aras Kargo">Aras Kargo</option>
                      <option value="Yurtiçi Kargo">Yurtiçi Kargo</option>
                      <option value="PTT Kargo">PTT Kargo</option>
                      <option value="Sürat Kargo">Sürat Kargo</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-1">Takip Numarası</label>
                    <input
                      type="text"
                      value={cargoTracking}
                      onChange={(e) => setCargoTracking(e.target.value)}
                      placeholder="Kargo takip numarası"
                      className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCargoForm(false)}
                      className="px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Kaydet
                    </button>
                    <button
                      onClick={() => setShowCargoForm(false)}
                      className="px-4 py-2 text-sm text-stone-500 hover:text-stone-700 cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Messages */}
          <section>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Mesaj Geçmişi</h4>
            <div className="bg-stone-50 rounded-xl p-4 space-y-3 max-h-[240px] overflow-y-auto">
              {mockMessages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                    msg.type === 'sent'
                      ? 'bg-gold-500 text-white rounded-br-md'
                      : 'bg-white border border-stone-200 text-stone-700 rounded-bl-md'
                  }`}>
                    <p>{msg.content}</p>
                    <span className={`text-[10px] mt-1 block ${msg.type === 'sent' ? 'text-white/70' : 'text-stone-400'}`}>
                      {msg.time} · {msg.date}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Bottom Action Bar */}
        <div className="sticky bottom-0 bg-white border-t border-stone-200 p-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => openWhatsApp(getMessageTemplate('daily'))}
              className="px-3 py-2 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-chat-3-line" /> Günlük Bilgilendirme
            </button>
            <button
              onClick={() => openWhatsApp(getMessageTemplate('drawing'))}
              className="px-3 py-2 bg-status-purple text-white text-xs font-medium rounded-lg hover:bg-status-purple/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-pencil-ruler-line" /> Çizim Mesajı
            </button>
            <button
              onClick={() => openWhatsApp(getMessageTemplate('cutting'))}
              className="px-3 py-2 bg-status-orange text-white text-xs font-medium rounded-lg hover:bg-status-orange/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-scissors-cut-line" /> Kesim Mesajı
            </button>
            <button
              onClick={() => openWhatsApp(getMessageTemplate('cargo'))}
              className="px-3 py-2 bg-status-sky text-white text-xs font-medium rounded-lg hover:bg-status-sky/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-truck-line" /> Kargo Mesajı
            </button>

            {/* Status Update */}
            <div className="relative">
              <button
                onClick={() => setStatusDropdown(!statusDropdown)}
                className="px-3 py-2 bg-gold-500 text-white text-xs font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-arrow-left-right-line" /> Durumu Güncelle
              </button>
              {statusDropdown && (
                <div className="absolute bottom-full left-0 mb-2 w-56 bg-white border border-stone-200 rounded-xl shadow-xl py-1 z-20">
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setStatusDropdown(false)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-stone-50 transition-colors cursor-pointer ${
                        key === order.status ? 'text-gold-600 font-medium bg-gold-50' : 'text-stone-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="px-3 py-2 bg-status-green text-white text-xs font-medium rounded-lg hover:bg-status-green/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-bank-card-line" /> Ödeme Alındı
            </button>

            <button
              className="px-3 py-2 bg-status-red text-white text-xs font-medium rounded-lg hover:bg-status-red/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-close-circle-line" /> İptal Et
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function cargoStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    bekliyor: 'Kargo Bekliyor',
    hazir: 'Kargoya Hazır',
    verildi: 'Kargoya Verildi',
    yolda: 'Yolda',
    dagitimda: 'Dağıtımda',
    teslim_edildi: 'Teslim Edildi',
    teslim_edilemedi: 'Teslim Edilemedi',
    iade_surecinde: 'İade Sürecinde',
    iade_geldi: 'İade Geldi',
  };
  return labels[status] || status;
}